import { useEffect, useRef, useState } from 'react';
import Papa from 'papaparse';

// types
import type { ZipCodeSearchProps } from './zip-code-search.types';

// utils
import ZipCodeSearchView from './zip-code-search-view';
import { csvSearch } from './tracking-actions';
import { isEmpty } from 'utils/is-empty';
import { useTracking } from 'utils/hooks/useTracking';
import { useIsPreview } from 'utils/hooks/useIsPreview';

export function ZipCodeSearch({
  exactMatch,
  fileUrl,
  errorMessage,
  ...props
}: Readonly<ZipCodeSearchProps>) {
  const [results, setResults] = useState<{ data: string[][] } | Record<string, never>>({});
  const [hits, setHits] = useState<string[][]>([]);
  const [mounted, setMounted] = useState(false);
  const [curErrorMessage, setCurErrorMessage] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const track = useTracking();
  const isPreview = useIsPreview();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    getCSV();
  }, [fileUrl]);

  useEffect(() => {
    if (isEmpty(hits) && mounted) {
      setCurErrorMessage(errorMessage);
    } else {
      setCurErrorMessage('');
    }
  }, [hits]);

  const onSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { current } = inputRef;
    const { value } = current || { value: '' };

    e.preventDefault();

    if (!isEmpty(value) && !isEmpty(results)) {
      const dataWithoutTitle = results.data.slice(1, results.data.length);

      setHits(
        dataWithoutTitle.filter((item) =>
          exactMatch ? item[0] === value : item[0].includes(value),
        ),
      );
    } else {
      setCurErrorMessage(errorMessage);
      setHits([]);
    }

    track.trackEvent(csvSearch((e.target as HTMLInputElement).value));
  };

  const getCSV = () => {
    if (isEmpty(fileUrl)) {
      return;
    }

    if (fileUrl.includes('.csv')) {
      Papa.parse(fileUrl, {
        download: true,
        ...(isPreview ? { withCredentials: true } : {}),
        complete: (r) => setResults(r),
      });
    } else {
      Papa.parse(fileUrl, {
        complete: (r) => setResults(r),
      });
    }
  };

  return (
    <ZipCodeSearchView
      {...props}
      headers={results?.data?.[0] ?? []}
      hits={hits}
      onSearch={onSearch}
      inputRef={inputRef}
      errorMessage={curErrorMessage}
    />
  );
}
